import React, { useEffect, useState } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadUser } from "../../store/slices/authSlice";
import { X, ChevronDown } from "lucide-react";
import axios from "../../utils/axiosConfig";

const SidebarDrawer = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { slug } = useParams();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const to = (path = "") => `/${slug}${path}`;

  const [cliente, setCliente] = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const resp = await axios.get(`/api/clients/${slug}`);
        if (alive) setCliente(resp.data);
      } catch { }
      finally {
        if (alive) setLoadingCliente(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // Refrescar datos del usuario al abrir el sidebar para captar cambios de permisos
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      dispatch(loadUser());
    }
  }, [isOpen, isAuthenticated, dispatch]);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/40 z-[60] transition-opacity duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
      />

      {/* Drawer */}
      <aside
        className={`fixed z-[70] top-0 left-0 h-full w-72 bg-surface shadow-2xl border-r border-white/5
        transition-transform duration-300 ease-in-out
        ${isOpen ? "translate-x-0" : "-translate-x-full"}`}
        aria-hidden={!isOpen}
        aria-label="Sidebar de navegación"
      >
        {/* WRAPPER para poder fijar footer abajo */}
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-white/5 bg-background/50">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative">
                <img
                  src={cliente?.logoUrl || "/logolm.jpg"}
                  alt={cliente?.nombre || "Logo"}
                  className="w-10 h-10 rounded-full object-cover border border-white/10"
                />
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-surface rounded-full"></div>
              </div>
              <span
                className="font-bold text-primary-orange truncate"
                title={cliente?.nombre || ""}
              >
                {loadingCliente ? "Cargando…" : (cliente?.nombre || "—")}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-white/5 text-white/50 hover:text-white transition-colors"
              aria-label="Cerrar menú"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navegación */}
          <nav className="px-3 py-6 space-y-2 flex-1 overflow-y-auto">
            <SidebarLink to="/explorar" onClick={onClose} highlight>
              Explorar Socios
            </SidebarLink>
            <div className="py-2" />
            <SidebarLink to={to("")} onClick={onClose}>Inicio</SidebarLink>

            {cliente?.organizationType !== "sports" && (
              <SidebarLink to={to("/events")} onClick={onClose}>Eventos</SidebarLink>
            )}

            {isAuthenticated && (
              <SidebarLink to={to("/profile")} onClick={onClose}>Perfil</SidebarLink>
            )}

            {isAuthenticated && cliente?.organizationType !== "sports" && (
              user.role === "rrpp" || 
              user.memberships?.some(m => m.clientId?.toString() === cliente?._id?.toString() && m.role === "rrpp")
            ) && (
              <SidebarLink to={to("/rrpp/dashboard")} onClick={onClose} highlight>
                Panel RRPP
              </SidebarLink>
            )}

            {user && (
              (() => {
                const currentClientId = cliente?._id;
                if (!currentClientId) return null;

                // 1. Buscar membresía para este salón específico
                const membership = user.memberships?.find(
                  m => m.clientId && m.clientId.toString() === currentClientId.toString()
                );

                // 2. Determinar roles LOCALES
                const isGlobalAdmin = user.role === 'admin' && !user.clientId; // SuperAdmin legado
                const isLegacyTenantAdmin = user.role === 'admin' && user.clientId && user.clientId.toString() === currentClientId.toString();

                const isTenantAdmin = membership?.role === 'admin' || isLegacyTenantAdmin;
                const isTenantEmployee = membership?.role === 'employee';

                // 3. Permisos explícitos (si existen en la membresía)
                const localPermissions = membership?.permissions || [];

                // Validar acceso general al menú admin
                const canSeeAdminMenu = isGlobalAdmin || isTenantAdmin || isTenantEmployee;

                if (!canSeeAdminMenu) return null;

                const hasPerm = (perm) => isGlobalAdmin || isTenantAdmin || localPermissions.includes(perm) || user.permissions?.includes(perm) /* legacy fallback */;


                return (
                  <div className="pt-4 mt-4 border-t border-white/5">
                    <p className="px-3 text-[10px] font-bold text-white/30 uppercase tracking-[2px] mb-2">Administración</p>

                    {hasPerm("ver_estadisticas") && (
                      <SidebarLink to={to("/admin/dashboard")} onClick={onClose} highlight>
                        Estadísticas
                      </SidebarLink>
                    )}

                    {hasPerm("gestionar_eventos") && (
                      <>
                        {cliente?.organizationType !== "sports" && (
                          <SidebarLink to={to("/admin/events")} onClick={onClose}>
                            Gestión de Eventos
                          </SidebarLink>
                        )}

                        {cliente?.organizationType !== "sports" && (
                          <SidebarLink to={to("/admin/agenda")} onClick={onClose}>
                            Agenda
                          </SidebarLink>
                        )}

                        {cliente?.organizationType === "venue" && (
                          <SidebarLink to={to("/admin/quotes")} onClick={onClose}>
                            Solicitudes
                          </SidebarLink>
                        )}

                        {cliente?.organizationType === "sports" && (
                          <SidebarLink to={to("/admin/tournaments")} onClick={onClose}>
                            Gestión de Torneos
                          </SidebarLink>
                        )}

                        {cliente?.organizationType === "sports" && (
                          <SidebarLink to={to("/admin/matches")} onClick={onClose}>
                            Gestión de Partidos
                          </SidebarLink>
                        )}

                        {/* Acordeón: Subir fotos - Oculto para creadores */}
                        {cliente?.organizationType !== "creator" && (
                           <>
                             <button
                               type="button"
                               onClick={() => setOpenUpload((v) => !v)}
                               className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-white/5 text-white/70 hover:text-white transition-all group"
                               aria-expanded={openUpload}
                               aria-controls="upload-submenu"
                             >
                               <span className="font-medium">Subir Fotos</span>
                               <ChevronDown
                                 className={`transition-transform duration-300 ${openUpload ? "rotate-180 text-primary-orange" : "text-white/30"}`}
                                 size={18}
                               />
                             </button>

                             <div
                               id="upload-submenu"
                               className={`overflow-hidden transition-all duration-300 ${openUpload ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}
                             >
                               {cliente?.organizationType !== "sports" && (
                                 <Link
                                   to={to("/admin/gallery?type=event")}
                                   onClick={onClose}
                                   className="flex items-center gap-3 ml-4 mt-1 px-4 py-2.5 rounded-xl text-sm text-white/50 hover:text-white hover:bg-white/5 transition-all group"
                                 >
                                   <div className="w-1.5 h-1.5 rounded-full bg-primary-orange/40 group-hover:bg-primary-orange transition-colors" />
                                   Fotos de eventos
                                 </Link>
                               )}
                               <Link
                                 to={to("/admin/gallery?type=venue")}
                                 onClick={onClose}
                                 className={`flex items-center gap-3 ml-4 ${cliente?.organizationType === "sports" ? "mt-1" : ""} px-4 py-2.5 rounded-xl text-sm text-white/50 hover:text-white hover:bg-white/5 transition-all group`}
                               >
                                 <div className="w-1.5 h-1.5 rounded-full bg-blue-500/40 group-hover:bg-blue-500 transition-colors" />
                                 Fotos del espacio
                               </Link>
                             </div>
                           </>
                        )}

                        {!["sports", "creator"].includes(cliente?.organizationType) && (
                          <SidebarLink to={to("/admin/bar-catalog")} onClick={onClose}>
                            Barra & Catálogo
                          </SidebarLink>
                        )}
                      </>
                    )}


                    {hasPerm("gestionar_staff") && !["sports", "creator"].includes(cliente?.organizationType) && (
                      <SidebarLink to={to("/admin/staff")} onClick={onClose}>
                        Gestión de Staff
                      </SidebarLink>
                    )}

                    {hasPerm("escanear_tickets") && cliente?.organizationType !== "sports" && (
                      <SidebarLink to={to("/admin/tickets")} onClick={onClose}>
                        Gestión de Tickets
                      </SidebarLink>
                    )}

                    {/* Admin RR.PP y Settings: Solo para admins (Global o Tenant) */}
                    {(isGlobalAdmin || isTenantAdmin) && (
                      <>
                        {!["sports", "creator"].includes(cliente?.organizationType) && (
                          <SidebarLink to={to("/admin/rrpp")} onClick={onClose}>
                            Gestión RRPP
                          </SidebarLink>
                        )}
                        {cliente?.organizationType !== "creator" && (
                          <SidebarLink to={to("/admin/billing")} onClick={onClose}>
                            Facturación y Planes
                          </SidebarLink>
                        )}
                        <SidebarLink to={to("/admin/settings")} onClick={onClose} highlight>
                          Ajustes
                        </SidebarLink>
                      </>
                    )}

                    {user.email === 'leonardodavidcba@gmail.com' && (
                      <div className="pt-4 mt-4 border-t border-white/10">
                        <SidebarLink to="/superadmin/dashboard" onClick={onClose} highlight>
                          SuperAdmin
                        </SidebarLink>
                      </div>
                    )}
                  </div>
                );
              })()
            )}
          </nav>

          {/* Footer fijo abajo */}
          <div className="border-t border-white/5 px-6 py-6 bg-background/30">
            <p className="text-[10px] font-bold text-white/30 uppercase tracking-widest mb-2">Ayuda</p>
            <Link
              to={to("/support")}
              onClick={onClose}
              className="inline-flex items-center gap-2 text-sm font-bold text-primary-orange hover:text-accent transition-colors group"
            >
              Ir a soporte
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
};

const SidebarLink = ({ to, onClick, children, highlight }) => (
  <NavLink
    to={to}
    end
    onClick={onClick}
    className={({ isActive }) => `block px-3 py-2.5 rounded-xl font-medium transition-all ${
      isActive
        ? "bg-brand-gradient text-white shadow-lg shadow-orange-500/10"
        : highlight
          ? "text-primary-orange/80 hover:text-primary-orange hover:bg-primary-orange/10"
          : "text-white/70 hover:text-white hover:bg-white/5"
    }`}
  >
    {children}
  </NavLink>
);

export default SidebarDrawer;


